/**
 * apiErrorEnvelope — leitor ÚNICO do envelope de erro que os serviços emitem.
 *
 * Existe porque a lógica estava duplicada dentro do `ApiClient`, as duas cópias
 * divergiram, e a que atendia o portal inteiro lia `{code, message}` — uma forma
 * que nenhum serviço deste repositório emite. O resultado prático era o usuário
 * ver `HTTP 403` na tela em vez da frase que o servidor mandou (LDG-0125).
 *
 * As formas realmente emitidas, e o arquivo que produz cada uma:
 *   - ProblemDetails RFC 7807 + extensão `code`
 *       Organization.Api/Middleware/OrganizationExceptionMiddleware.cs
 *   - `{error, code, correlationId}`
 *       AccountManagement.Api/Middleware/ExceptionHandlingMiddleware.cs
 *       ActivityManagement.Api/Middleware/GlobalExceptionHandlerMiddleware.cs
 *   - ProblemDetails + extensão `errorCode`
 *       TenantAdministration.Api/Infrastructure/TenantAdministrationProblemDetails.cs
 *       AuditLog.Api/ErrorHandling/AuditExceptionHandler.cs
 *   - variante snake (`error_code`, `correlation_id`, `field`)
 *       OpportunityPipeline.Contracts/ErrorCodes/ErrorCodes.cs
 *
 * Duas guardas de segurança, e as duas são deliberadas. Em 5xx o texto do servidor
 * é DESCARTADO para a mensagem de tela: `detail` de 500 costuma trazer nome de
 * constraint, SQLSTATE ou fragmento de stack, e isso não vai para o usuário — o
 * técnico continua disponível em `ApiError.detail`. E os títulos genéricos que o
 * ASP.NET preenche sozinho são ignorados, porque exibi-los em inglês é pior que a
 * frase do catálogo em português.
 */

import {
  mapStatusToErrorCode,
  SESSION_EXPIRED,
  type ErrorContext,
} from "../adapters/http/errorCodeMap.js";
import { t } from "../shared/i18n/index.js";

/** Envelope bruto, na união de todas as formas emitidas. Todo campo é opcional. */
export interface RawErrorBody {
  readonly code?: unknown;
  readonly errorCode?: unknown;
  readonly error_code?: unknown;
  readonly detail?: unknown;
  readonly error?: unknown;
  readonly message?: unknown;
  readonly title?: unknown;
  readonly correlationId?: unknown;
  readonly correlation_id?: unknown;
  readonly traceId?: unknown;
  readonly field?: unknown;
}

/** Resultado normalizado, pronto para virar `ApiError`. */
export interface NormalizedApiError {
  readonly code: string;
  readonly message: string;
  readonly correlationId?: string;
  readonly detail?: string;
  readonly field?: string;
}

/**
 * Títulos que o ASP.NET preenche sozinho quando o handler não define um.
 * Em inglês e sem informação — a frase do catálogo é melhor para o usuário.
 */
const TITULOS_GENERICOS = new Set([
  "one or more validation errors occurred.",
  "an error occurred while processing your request.",
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "conflict",
  "internal server error",
  "unprocessable entity",
]);

function texto(valor: unknown): string | undefined {
  return typeof valor === "string" && valor.trim().length > 0 ? valor.trim() : undefined;
}

function primeiro(...valores: unknown[]): string | undefined {
  for (const v of valores) {
    const s = texto(v);
    if (s !== undefined) return s;
  }
  return undefined;
}

/**
 * Lê o corpo da resposta com tolerância. Devolve `null` quando não há JSON —
 * corpo vazio de um 403, HTML de 502 do nginx, ou payload truncado. Nunca lança:
 * uma falha de parsing não pode substituir o erro real por um `SyntaxError`.
 *
 * Prefere `text()` a `json()` porque `text()` permite distinguir "corpo vazio" de
 * "JSON inválido", e porque o corpo só pode ser consumido uma vez.
 */
export async function readErrorBody(response: Response): Promise<RawErrorBody | null> {
  try {
    if (typeof response.text === "function") {
      const bruto = await response.text();
      if (!bruto || bruto.trim().length === 0) return null;
      return JSON.parse(bruto) as RawErrorBody;
    }
    if (typeof response.json === "function") {
      return (await response.json()) as RawErrorBody;
    }
  } catch {
    // Corpo ausente, não-JSON ou truncado — o fallback do catálogo cobre.
  }
  return null;
}

/** Frase pt-BR do catálogo WEB-ERR-* para um status, quando o servidor não deu palavras usáveis. */
export function statusFallbackMessage(status: number, context: ErrorContext = "generic"): string {
  // 404 é a exceção documentada: `mapStatusToErrorCode` manda 404 para WEB-ERR-011
  // ("Recurso ainda não disponível"), que lê como funcionalidade inacabada. Para um
  // item que não existe — ou que o tenant não enxerga — a frase certa é outra.
  if (status === 404) return t("error.WEB-ERR-014");

  const codigo = mapStatusToErrorCode(status, context);
  const chave = codigo === SESSION_EXPIRED ? "error.WEB-ERR-002" : `error.${codigo}`;
  return t(chave);
}

/**
 * Converte status + corpo bruto no par (código, mensagem) que a tela consome.
 *
 * Precedência da mensagem: `detail` → `error` → `message` → `title`. `detail` vem
 * primeiro porque é onde o ProblemDetails carrega a frase específica; `title` vem
 * por último porque costuma ser a categoria genérica.
 */
export function normalizeApiError(
  status: number,
  body: RawErrorBody | null,
  context: ErrorContext = "generic",
): NormalizedApiError {
  const code = primeiro(body?.code, body?.errorCode, body?.error_code) ?? "UNKNOWN_ERROR";
  const correlationId = primeiro(body?.correlationId, body?.correlation_id, body?.traceId);
  const field = primeiro(body?.field);
  const detalheTecnico = primeiro(body?.detail);

  // Em 5xx e sem corpo, a mensagem vem SEMPRE do catálogo.
  const podeUsarTextoDoServidor = status < 500 && body !== null;

  let message: string | undefined;
  if (podeUsarTextoDoServidor) {
    message = primeiro(body?.detail, body?.error, body?.message);
    if (message === undefined) {
      const titulo = primeiro(body?.title);
      if (titulo !== undefined && !TITULOS_GENERICOS.has(titulo.toLowerCase())) {
        message = titulo;
      }
    }
  }

  return {
    code,
    message: message ?? statusFallbackMessage(status, context),
    ...(correlationId !== undefined && { correlationId }),
    ...(detalheTecnico !== undefined && { detail: detalheTecnico }),
    ...(field !== undefined && { field }),
  };
}
